import React, { useState, useEffect } from 'react';
import api from '@/api/axios';

/*
 * 인증이 필요한 파일을 이미지로 그린다.
 *
 * `/files/download/{id}` 는 Authorization 헤더가 있어야 열린다(익명 접근을 막았다).
 * <img src="/files/download/..."> 로 바로 걸면 브라우저가 토큰을 실어 주지 않아 401 이 나고,
 * 사진이 통째로 안 뜬다. 그래서 blob 으로 받아 objectURL 로 그린다.
 *
 * 받아 둔 사진은 fileId 별로 기억한다. 같은 fileId 는 늘 같은 사진이라(내용이 바뀌면
 * 새 fileId 가 생긴다) 캐시해도 안전하고, 같은 사진을 화면마다 다시 받지 않는다.
 * (UserAvatar 와 같은 방식)
 */
const cache = new Map();      // fileId -> objectURL | null(못 받음)
const inFlight = new Map();   // fileId -> Promise

const loadFileImage = (fileId) => {
  if (cache.has(fileId)) return Promise.resolve(cache.get(fileId));
  if (inFlight.has(fileId)) return inFlight.get(fileId);

  const p = api.get(`/files/download/${fileId}`, { responseType: 'blob' })
    .then((res) => {
      const url = URL.createObjectURL(res.data);
      cache.set(fileId, url);
      return url;
    })
    .catch(() => {
      // 못 받은 것도 기억해 둔다. 안 그러면 없는 사진을 화면마다 계속 다시 조른다.
      cache.set(fileId, null);
      return null;
    })
    .finally(() => { inFlight.delete(fileId); });

  inFlight.set(fileId, p);
  return p;
};

/**
 * @param {number|string} fileId  볼 파일 id (없으면 fallback 을 그린다)
 * @param {string} alt
 * @param {string} className      <img> 에 그대로 넘긴다
 * @param {object} style
 * @param {React.ReactNode} fallback  사진이 없거나 못 불러올 때 그릴 것 (기본: 아무것도 안 그림)
 */
const FileImage = ({ fileId, alt = '', className, style, fallback = null }) => {
  const [src, setSrc] = useState(() => (fileId ? cache.get(fileId) ?? null : null));

  useEffect(() => {
    if (!fileId) { setSrc(null); return undefined; }
    if (cache.has(fileId)) { setSrc(cache.get(fileId)); return undefined; }

    let cancelled = false;
    loadFileImage(fileId).then((url) => { if (!cancelled) setSrc(url); });

    // objectURL 은 캐시가 계속 쓰므로 여기서 해제하지 않는다.
    return () => { cancelled = true; };
  }, [fileId]);

  if (src) {
    return <img src={src} alt={alt} className={className} style={style} />;
  }
  return fallback;
};

export default FileImage;
